import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Building, CreditCard, ShieldCheck, Globe, Loader } from 'lucide-react';

const INDIAN_STATES = [
    "Andaman and Nicobar", "Andhra Pradesh", "Andhra Pradesh (New)", "Arunachal Pradesh", "Assam",
    "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli", "Daman and Diu",
    "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
    "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh",
    "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
    "Other Territory", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
    "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const BillingAddressModal = ({ isOpen, onClose, onConfirm, planName, loading }) => {
    const [address, setAddress] = useState({
        address_line1: '',
        address_line2: '',
        city: '',
        state: 'Madhya Pradesh',
        postal_code: '',
        country: 'India',
        country_code: 'IN',
        gstin: '',
        company_name: ''
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAddress(prev => ({ ...prev, [name]: value }));
        // Clear error when typing
        if (errors[name]) {
            setErrors(prev => {
                const newErrs = { ...prev };
                delete newErrs[name];
                return newErrs;
            });
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!address.address_line1 || address.address_line1.length < 5) {
            newErrors.address_line1 = 'Address line 1 must be at least 5 characters';
        }
        if (!address.city || address.city.length < 2) {
            newErrors.city = 'City is required';
        }
        if (address.country_code === 'IN' && !address.state) {
            newErrors.state = 'State is required for India';
        }
        if (!address.postal_code) {
            newErrors.postal_code = 'Postal code is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onConfirm(address);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
                />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    style={{
                        position: 'relative',
                        background: '#111',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '24px',
                        width: '100%',
                        maxWidth: '600px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        padding: '40px',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
                    }}
                >
                    <button 
                        onClick={onClose}
                        style={{ position: 'absolute', top: '24px', right: '24px', background: 'transparent', border: 'none', color: '#666', cursor: 'pointer' }}
                    >
                        <X size={24} />
                    </button>

                    <div style={{ marginBottom: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <div style={{ background: 'rgba(245, 158, 11, 0.1)', p: '8px', borderRadius: '12px', display: 'flex', padding: '8px' }}>
                                <MapPin size={24} color="#f59e0b" />
                            </div>
                            <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#fff', margin: 0 }}>BILLING ADDRESS</h2>
                        </div>
                        <p style={{ color: '#999', fontSize: '14px' }}>Please provide your address for GST-compliant invoice generation for <strong>{planName}</strong> plan.</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            {/* Address Line 1 */}
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={labelStyle}>ADDRESS LINE 1 *</label>
                                <input
                                    type="text"
                                    name="address_line1"
                                    value={address.address_line1}
                                    onChange={handleChange}
                                    placeholder="House No, Building, Street"
                                    style={inputStyle(errors.address_line1)}
                                />
                                {errors.address_line1 && <span style={errorStyle}>{errors.address_line1}</span>}
                            </div>

                            {/* Address Line 2 */}
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={labelStyle}>ADDRESS LINE 2 (OPTIONAL)</label>
                                <input
                                    type="text"
                                    name="address_line2"
                                    value={address.address_line2}
                                    onChange={handleChange}
                                    placeholder="Area, Landmark"
                                    style={inputStyle()}
                                />
                            </div>

                            {/* City */}
                            <div>
                                <label style={labelStyle}>CITY *</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={address.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                    style={inputStyle(errors.city)}
                                />
                                {errors.city && <span style={errorStyle}>{errors.city}</span>}
                            </div>

                            {/* State */}
                            <div>
                                <label style={labelStyle}>STATE *</label>
                                {address.country_code === 'IN' ? (
                                    <select
                                        name="state"
                                        value={address.state}
                                        onChange={handleChange}
                                        style={inputStyle(errors.state)}
                                    >
                                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        name="state"
                                        value={address.state}
                                        onChange={handleChange}
                                        placeholder="State/Province"
                                        style={inputStyle(errors.state)}
                                    />
                                )}
                                {errors.state && <span style={errorStyle}>{errors.state}</span>}
                            </div>

                            {/* Country */}
                            <div>
                                <label style={labelStyle}>COUNTRY *</label>
                                <div style={{ position: 'relative' }}>
                                    <select
                                        name="country_code"
                                        value={address.country_code}
                                        onChange={(e) => {
                                            const code = e.target.value;
                                            setAddress(prev => ({ 
                                                ...prev, 
                                                country_code: code, 
                                                country: code === 'IN' ? 'India' : 'International',
                                                state: code === 'IN' ? 'Madhya Pradesh' : ''
                                            }));
                                        }}
                                        style={inputStyle()}
                                    >
                                        <option value="IN">India</option>
                                        <option value="US">United States</option>
                                        <option value="GB">United Kingdom</option>
                                        <option value="CA">Canada</option>
                                        <option value="AU">Australia</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                            </div>

                            {/* Postal Code */}
                            <div>
                                <label style={labelStyle}>POSTAL CODE *</label>
                                <input
                                    type="text"
                                    name="postal_code"
                                    value={address.postal_code}
                                    onChange={handleChange}
                                    placeholder="PIN / Zip Code"
                                    style={inputStyle(errors.postal_code)}
                                />
                                {errors.postal_code && <span style={errorStyle}>{errors.postal_code}</span>}
                            </div>

                            {/* GSTIN */}
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={labelStyle}>GSTIN (FOR B2B - OPTIONAL)</label>
                                <input
                                    type="text"
                                    name="gstin"
                                    value={address.gstin}
                                    onChange={handleChange}
                                    placeholder="Enter your 15-digit GSTIN"
                                    style={inputStyle()}
                                />
                            </div>

                            {/* Company Name */}
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={labelStyle}>COMPANY NAME (OPTIONAL)</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={address.company_name}
                                    onChange={handleChange}
                                    placeholder="Enterprise Name"
                                    style={inputStyle()}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <ShieldCheck size={20} color="#10b981" />
                                <span style={{ color: '#aaa', fontSize: '12px' }}>GST will be added on top of the plan price based on your state. Your details are secure and used only for invoicing.</span>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '16px',
                                    borderRadius: '14px',
                                    background: '#f59e0b',
                                    color: '#000',
                                    fontWeight: '900',
                                    fontSize: '16px',
                                    border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px'
                                }}
                            >
                                {loading ? <Loader className="spin" size={20} /> : <CreditCard size={20} />}
                                {loading ? 'PREPARING SECURE CHECKOUT...' : 'PROCEED TO PAYMENT'}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: '900',
    color: '#666',
    marginBottom: '8px',
    letterSpacing: '0.5px'
};

const inputStyle = (hasError) => ({
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: `1px solid ${hasError ? '#ef4444' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: '12px',
    padding: '12px 16px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s',
    fontFamily: 'inherit'
});

const errorStyle = {
    color: '#ef4444',
    fontSize: '11px',
    marginTop: '4px',
    fontWeight: '600'
};

export default BillingAddressModal;
